import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@emotion/react";
import {
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { HiOutlineArrowsRightLeft } from "react-icons/hi2";
import {
  MobileBadge,
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  MobilePagination,
} from "../../../../components/MobileList";
import { MdOutlineClear } from "react-icons/md";
import { BiInfoCircle } from "react-icons/bi";
import { BsFillFileEarmarkExcelFill } from "react-icons/bs";
import {
  Wallet,
  setTransactionsSearchBody,
} from "../../../../app/slices/walletSlice";
import DateRangepicker from "../../../../helpers/dateRangePicker";
import { SelectRepresentative } from "../../../../components/Selectors/SelectRepresentative";
import { SelectTransactionTypes } from "../../../../components/Selectors/SelectTransactionTypes";
import { SelectTeamLeader } from "../../../../components/Selectors/SelectTeamLeader";
import { restoreFilters } from "../../../../helpers/restoreFilters";
// Removed unused imports (Permission, TransactionForm)

const PointsTransactionsTable = () => {
  // No specific user id required for points transactions listing
  const dispatch = useDispatch();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const transactionsSearchBody = useSelector(
    (state) => state.wallet.transactionsSearchBody,
  );
  // transactionsSearchBody is shared with the user transactions page; only
  // restore what this page wrote (without_pins). Keys are renamed/stringified.
  const [search, setSearch] = useState(() => {
    const saved = transactionsSearchBody.without_pins
      ? {
          ...transactionsSearchBody,
          startDate: transactionsSearchBody.start_date,
          endDate: transactionsSearchBody.end_date,
          type: transactionsSearchBody.type
            ? JSON.parse(transactionsSearchBody.type)
            : undefined,
        }
      : {};
    return restoreFilters(
      {
        search: "",
        order_id: "",
        user_type: null,
        date: "",
        type: null,
        startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
        skip: 0,
        transaction_number: "",
        rep_id: null,
        min_amount: "",
        team_leader_id: null,
      },
      saved,
    );
  });
  const _Loading = useSelector((state) => state.wallet.wallet.loading);
  const _Transactions = useSelector(
    (state) => state.wallet.transactionsWithoutPins,
  );
  const _rep = useSelector((state) => state.user.usersList.data);
  const _TransactionTypes = useSelector(
    (state) => state.wallet.transactionType,
  );
  const _TeamLeader = useSelector((state) => state.team.leaders);

  const columns = [
    {
      field: "sequence",
      headerName: "التسلسل",
      flex: 0.3,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "number", headerName: "رقم المناقلة", flex: 0.5 },
    {
      field: "amount",
      headerName: "القيمة (IQD)",
      flex: 0.5,
      renderCell: (params) => (
        <Typography>{params.row.amount?.toLocaleString()}</Typography>
      ),
    },
    {
      field: "by_user_name",
      headerName: "بواسطة",
      flex: 1,
      renderCell: (params) => (
        <Typography>{params.row.by_user_name}</Typography>
      ),
    },
    {
      field: "from_user_name",
      headerName: "من المحفظة",
      flex: 1,
    },
    {
      field: "to_user_name",
      headerName: "الى المحفظة",
      flex: 1,
    },
    {
      field: "from_user",
      headerName: "حساب المرسل (IQD)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            <Typography>
              الرصيد: {params.row.from_current_balance?.toLocaleString()}
            </Typography>
            <Typography>
              الديون :{params.row.from_current_debt?.toLocaleString()}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "to_user",
      headerName: "حساب المستلم (IQD)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            <Typography>
              الرصيد: {params.row.to_current_balance?.toLocaleString()}
            </Typography>
            <Typography>
              الديون :{params.row.to_current_debt?.toLocaleString()}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "created_at",
      headerName: "تاريخ العملية",
      flex: 1,

      renderCell: (params) => (
        <Stack direction="column" spacing={1}>
          <Typography>
            {dayjs(params.row.created_at).format("YYYY-MM-DD ")}
          </Typography>
          <Typography variant="caption">
            {dayjs(params.row.created_at).format("HH:mm a")}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "type",
      headerName: "نوع العملية",
      flex: 1,
      renderCell: (params) => (
        <Stack>
          <Stack direction={"row"} spacing={1}>
            <Typography>{params.row.type}</Typography>
            {params.row.note && (
              <Tooltip title={params.row.note}>
                <IconButton size="small">
                  <BiInfoCircle />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      ),
    },
    { field: "order_id", headerName: "رقم الطلب", flex: 0.5 },
  ];

  useEffect(() => {
    dispatch(
      setTransactionsSearchBody({
        order_id: search.order_id,
        rep_id: search.rep_id ?? null,
        date: search.date,
        skip: currentPage * pageSize,
        take: pageSize,
        type: search.type ? JSON.stringify(search.type) : "",
        start_date: search.startDate,
        end_date: search.endDate,
        transaction_number: search.transaction_number,
        search: search.search,
        min_amount: search.min_amount,
        team_leader_id: search.team_leader_id,
        without_pins: 1,
      }),
    );
  }, [currentPage, dispatch, pageSize, search]);

  const handleSearch = () => {
    dispatch(Wallet.getAllTransactionWithoutPins(transactionsSearchBody));
  };

  // No user fetch needed

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          القيمة الكلية : {_Transactions.total_amount.toLocaleString()} (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} md={2.4}>
            <TextField
              size="small"
              value={search.search}
              onChange={(e) => setSearch({ ...search, search: e.target.value })}
              label="رقم نقطة، اسم نقطة بيع او رقم هاتف"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              size="small"
              value={search.transaction_number}
              onChange={(e) =>
                setSearch({ ...search, transaction_number: e.target.value })
              }
              label="بحث عن رقم المناقلة"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              size="small"
              value={search.order_id}
              onChange={(e) =>
                setSearch({ ...search, order_id: e.target.value })
              }
              label="بحث عن رقم الطلب"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <SelectTeamLeader
              supervisor_id={"true"}
              onChange={(event, value) => {
                setSearch({
                  ...search,
                  team_leader_id: value ? value.id : null,
                });
              }}
              value={
                _TeamLeader.data?.filter(
                  (x) => x.id === search.team_leader_id,
                )[0] ?? null
              }
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <SelectRepresentative
              onChange={(event, value) => {
                setSearch({
                  ...search,
                  rep_id: value ? value.id : null,
                });
              }}
              value={_rep.filter((x) => x.id === search.rep_id)[0] ?? null}
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <SelectTransactionTypes
              multiple
              onChange={(event, value) => {
                var ids = value.map((x) => x.key);
                setSearch({
                  ...search,
                  type: value ? ids : null,
                });
              }}
              value={
                _TransactionTypes?.filter((x) =>
                  search.type?.includes(x.key),
                ) ?? []
              }
              // value={search.type}
              // onChange={(e, newValue) => {
              //   setSearch({
              //     ...search,
              //     type: newValue ? newValue : null
              //   })
              // }}
            />
          </Grid>
          <Grid item xs={12} md={4.8}>
            <DateRangepicker
              startDate={search.startDate}
              endDate={search.endDate}
              handleOnChange={(searchDate) => {
                setSearch({
                  ...search,
                  startDate: dayjs(searchDate[0].startDate).format(
                    "YYYY-MM-DD",
                  ),
                  endDate: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              size="small"
              value={search.min_amount}
              onChange={(e) =>
                setSearch({ ...search, min_amount: e.target.value })
              }
              label="الحد الأدنى للمبلغ"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
              >
                بحث
              </Button>
              <Tooltip title="تصدير ملف اكسل">
                <IconButton
                  onClick={() =>
                    dispatch(Wallet.exportExcel(transactionsSearchBody))
                  }
                >
                  <BsFillFileEarmarkExcelFill />
                </IconButton>
              </Tooltip>
              <Tooltip title="الغاء الفلتر">
                <IconButton
                  onClick={() => {
                    setSearch({
                      search: "",
                      order_id: "",
                      user_type: null,
                      date: "",
                      type: null,
                      startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
                      endDate: dayjs().format("YYYY-MM-DD"),
                      skip: 0,
                      take: 15,
                      transaction_number: "",
                      rep_id: null,
                      min_amount: "",
                    });
                    setPageSize(15);
                    setCurrentPage(0);
                  }}
                >
                  <MdOutlineClear />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          {smallScreen ? (
            <Grid item xs={12}>
              <MobileListShell>
                {_Transactions?.data?.length > 0 ? (
                  <>
                    {_Transactions.data.map((row, index) => (
                      <MobileListItem
                        key={index}
                        avatar={<HiOutlineArrowsRightLeft />}
                        title={`مناقلة #${row.number}`}
                        subtitle={dayjs(row.created_at).format(
                          "YYYY-MM-DD · hh:mm a"
                        )}
                        badge={
                          row.type ? <MobileBadge label={row.type} /> : null
                        }
                        highlight={{
                          label: "القيمة",
                          value: (row.amount ?? 0).toLocaleString(),
                          unit: "IQD",
                        }}
                        fields={[
                          { label: "التسلسل", value: index + 1 },
                          { label: "رقم الطلب", value: row.order_id },
                          { label: "من المحفظة", value: row.from_user_name },
                          { label: "الى المحفظة", value: row.to_user_name },
                        ]}
                        note={row.note}
                      />
                    ))}
                    <MobilePagination
                      count={_Transactions.total ?? 0}
                      rowsPerPage={pageSize}
                      page={currentPage}
                      onPageChange={(e, newPage) => setCurrentPage(newPage)}
                      rowsPerPageOptions={[15, 25, 50, 100]}
                      onRowsPerPageChange={(event) =>
                        setPageSize(parseInt(event.target.value, 10))
                      }
                    />
                  </>
                ) : (
                  <MobileEmpty />
                )}
              </MobileListShell>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <div style={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  rows={_Transactions.data}
                  columns={columns}
                  pageSize={pageSize}
                  page={currentPage}
                  rowCount={_Transactions.total}
                  loading={_Loading}
                  rowHeight={65}
                  pagination
                  paginationMode="server"
                  selectionModel={[]}
                  onPageChange={(newPage) => setCurrentPage(newPage)}
                  rowsPerPageOptions={[15, 25, 50, 100]}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  components={{
                    Footer: CustomFooter,
                  }}
                />
              </div>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PointsTransactionsTable;
